import { Vector3 } from '@babylonjs/core';
import { World } from '../core/World';
import { InputManager } from '../core/InputManager';
import { BlockPicker, BlockHit } from './BlockPicker';
import { BuildPreview } from './BuildPreview';
import { Block } from '../voxel/Block';

export class BuildSystem {
  private world: World;
  private inputManager: InputManager;
  private blockPicker: BlockPicker;
  private buildPreview: BuildPreview;

  private selectedBlockId: number = Block.GRASS;
  private currentHit: BlockHit | null = null;

  // Hotbar
  private hotbar: number[] = [
    Block.GRASS,
    Block.DIRT,
    Block.STONE,
    Block.WOOD,
    Block.PLANKS,
    Block.BRICK,
    Block.COBBLESTONE,
    Block.GLASS,
    Block.SAND,
  ];
  private selectedSlot: number = 0;

  // Build cooldown
  private lastBuildTime: number = 0;
  private buildCooldown: number = 100; // ms

  constructor(world: World) {
    this.world = world;
    this.inputManager = InputManager.getInstance();
    this.blockPicker = new BlockPicker(world);
    this.buildPreview = new BuildPreview(world.getScene());

    this.selectedBlockId = this.hotbar[this.selectedSlot];
  }

  public update(eyePosition: Vector3, lookDirection: Vector3, playerPosition: Vector3): void {
    // Update block picking
    this.currentHit = this.blockPicker.pick(eyePosition, lookDirection);

    // Update preview
    if (this.currentHit) {
      const placementPos = this.blockPicker.getPlacementPosition(this.currentHit);

      // Don't show preview if it would be inside the player
      if (!this.isBlockInsidePlayer(placementPos, playerPosition)) {
        this.buildPreview.show(placementPos, this.selectedBlockId);
      } else {
        this.buildPreview.hide();
      }
    } else {
      this.buildPreview.hide();
    }

    // Handle input
    this.handleInput(playerPosition);
  }

  private handleInput(playerPosition: Vector3): void {
    const now = Date.now();

    // Only build when pointer is locked
    if (!this.inputManager.isPointerLockedState()) {
      return;
    }

    // Place block (right click)
    if (this.inputManager.isMouseButtonDown('right')) {
      if (now - this.lastBuildTime > this.buildCooldown) {
        this.placeBlock(playerPosition);
        this.lastBuildTime = now;
      }
    }

    // Remove block (left click)
    if (this.inputManager.isMouseButtonDown('left')) {
      if (now - this.lastBuildTime > this.buildCooldown) {
        this.removeBlock();
        this.lastBuildTime = now;
      }
    }

    // Hotbar selection with number keys
    for (let i = 0; i < 9; i++) {
      if (this.inputManager.isKeyPressed(`Digit${i + 1}`)) {
        this.selectSlot(i);
      }
    }

    // Scroll wheel to change slot
    const wheelDelta = this.inputManager.getWheelDelta();
    if (wheelDelta !== 0) {
      let newSlot = this.selectedSlot + wheelDelta;
      if (newSlot < 0) newSlot = 8;
      if (newSlot > 8) newSlot = 0;
      this.selectSlot(newSlot);
    }
  }

  private placeBlock(playerPosition: Vector3): boolean {
    if (!this.currentHit) return false;

    const placementPos = this.blockPicker.getPlacementPosition(this.currentHit);

    // Don't place inside player
    if (this.isBlockInsidePlayer(placementPos, playerPosition)) {
      return false;
    }

    // Don't place in invalid positions
    if (placementPos.y < 0 || placementPos.y > 15) {
      return false;
    }

    // Place the block
    const success = this.world.setBlock(
      placementPos.x,
      placementPos.y,
      placementPos.z,
      this.selectedBlockId
    );

    return success;
  }

  private removeBlock(): boolean {
    if (!this.currentHit) return false;

    // Don't remove bedrock
    if (this.currentHit.blockId === Block.BEDROCK) {
      return false;
    }

    // Remove the block (set to air)
    const success = this.world.setBlock(
      this.currentHit.position.x,
      this.currentHit.position.y,
      this.currentHit.position.z,
      Block.AIR
    );

    return success;
  }

  private isBlockInsidePlayer(blockPos: Vector3, playerPos: Vector3): boolean {
    const playerMinX = playerPos.x - 0.3;
    const playerMaxX = playerPos.x + 0.3;
    const playerMinY = playerPos.y;
    const playerMaxY = playerPos.y + 1.8;
    const playerMinZ = playerPos.z - 0.3;
    const playerMaxZ = playerPos.z + 0.3;

    const blockMinX = blockPos.x;
    const blockMaxX = blockPos.x + 1;
    const blockMinY = blockPos.y;
    const blockMaxY = blockPos.y + 1;
    const blockMinZ = blockPos.z;
    const blockMaxZ = blockPos.z + 1;

    return (
      playerMinX < blockMaxX &&
      playerMaxX > blockMinX &&
      playerMinY < blockMaxY &&
      playerMaxY > blockMinY &&
      playerMinZ < blockMaxZ &&
      playerMaxZ > blockMinZ
    );
  }

  public selectSlot(slot: number): void {
    if (slot >= 0 && slot < this.hotbar.length) {
      this.selectedSlot = slot;
      this.selectedBlockId = this.hotbar[slot];
      this.buildPreview.setBlockId(this.selectedBlockId);
    }
  }

  public setHotbarSlot(slot: number, blockId: number): void {
    if (slot >= 0 && slot < this.hotbar.length) {
      this.hotbar[slot] = blockId;
      if (slot === this.selectedSlot) {
        this.selectedBlockId = blockId;
        this.buildPreview.setBlockId(blockId);
      }
    }
  }

  public getHotbar(): number[] {
    return [...this.hotbar];
  }

  public getSelectedSlot(): number {
    return this.selectedSlot;
  }

  public getSelectedBlockId(): number {
    return this.selectedBlockId;
  }

  public getCurrentHit(): BlockHit | null {
    return this.currentHit;
  }

  public dispose(): void {
    this.buildPreview.dispose();
  }
}
